"use client";

import React, { useState, useMemo, useCallback, useEffect } from "react";
import { Typography } from "../../../components";
import { useTranslations } from "../../../contexts/translation.context";
import { FiltersSection, OrdersTable, PublicLinksModal } from "./components";
import { PartnerOrderData, OrdersFilters } from "./types";
import { transformPartnerOrderToOrderData } from "./utils";
import { DEFAULT_FILTERS, DEFAULT_PAGE_SIZE, OFFERS_PAGE_SIZE, SEARCH_DEBOUNCE_DELAY } from "./constants";
import partnerOfferService, { PartnerOrdersRequestParams } from "../../../api/partner/partnerOfferService";
import { usePageBreadcrumbs } from "../../../hooks/usePageBreadcrumbs";
import { useDisclosure } from "@heroui/react";
import { ContentPreviewModal } from "../../revshare/campaigns/components";
import { PartnerOrderActionType } from "./types";
import { showError, showPartnerSuccess } from "../../../utils/toast";

const Orders: React.FC = () => {
  const { t } = useTranslations();
  const [orders, setOrders] = useState<PartnerOrderData[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [totalCount, setTotalCount] = useState(0);

  const [filters, setFilters] = useState<OrdersFilters>(DEFAULT_FILTERS);

  // Local state for debounced search input
  const [localSearch, setLocalSearch] = useState(filters.search);

  const [availableOffers, setAvailableOffers] = useState<{ id: string; name: string }[]>([]);
  const [offersLoading, setOffersLoading] = useState(false);
  const { isOpen: isPublicLinksOpen, onOpen: onPublicLinksOpen, onClose: onPublicLinksClose } = useDisclosure();

  // Content preview modal state
  const [isContentModalOpen, setIsContentModalOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState<PartnerOrderData | null>(null);
  const [actionOrderId, setActionOrderId] = useState<string | null>(null);
  
  const breadcrumbs = useMemo(() => [
    { label: t('salesFans.home'), href: "/" },
    { label: t('sidebar.orders'), isActive: true }
  ], [t]);
  usePageBreadcrumbs(breadcrumbs);

  // Debounce for search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setFilters((prev) => ({ ...prev, search: localSearch }));
      setCurrentPage(1);
    }, SEARCH_DEBOUNCE_DELAY);

    return () => clearTimeout(timer);
  }, [localSearch]);

  // Sync local search with filters when they change externally
  useEffect(() => {
    setLocalSearch(filters.search);
  }, [filters.search]);

  // Load offers for filter
  useEffect(() => {
    const loadOffers = async () => {
      setOffersLoading(true);
      try {
        const response = await partnerOfferService.getOffers({
          page: 1,
          page_size: OFFERS_PAGE_SIZE,
        });

        if (response && response.objects && Array.isArray(response.objects)) {
          const offers = response.objects.map((offer: any) => ({
            id: offer.id,
            name: `${offer.id.slice(-4)} ${offer.source}`,
          }));
          setAvailableOffers(offers);
        } else {
          setAvailableOffers([]);
        }
      } catch (error) {
        console.error('Failed to load offers:', error);
        setAvailableOffers([]);
      } finally {
        setOffersLoading(false);
      }
    };

    loadOffers();
  }, []);

  // Prepare API params
  const apiParams = useMemo(() => {
    const params: PartnerOrdersRequestParams = {
      page: currentPage,
      page_size: pageSize,
    };

    if (filters.search) {
      params.search = filters.search;
    }

    if (filters.status !== 'all') {
      params.status = filters.status as any;
    }

    if (filters.offer !== 'all') {
      params.offer = filters.offer;
    }

    return params;
  }, [currentPage, pageSize, filters.search, filters.status, filters.offer]);

  const loadOrders = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await partnerOfferService.getOrders(apiParams);

      if (response && response.objects && Array.isArray(response.objects)) {
        const transformedOrders = response.objects.map(transformPartnerOrderToOrderData);
        setOrders(transformedOrders);
        setTotalCount(response.count || 0);
      } else {
        setOrders([]);
        setTotalCount(0);
      }
    } catch (error) {
      console.error('Failed to load orders:', error);
      setOrders([]);
      setTotalCount(0);
    } finally {
      setIsLoading(false);
    }
  }, [apiParams]);

  useEffect(() => {
    loadOrders();
  }, [loadOrders]);

  const getApiErrorMessage = useCallback(
    (error: any, fallbackMessage: string) => {
      const detail = error?.response?.data?.detail;
      if (typeof detail === 'string') {
        return detail;
      }
      if (Array.isArray(detail) && detail[0]?.msg) {
        return detail[0].msg;
      }
      return error?.message || fallbackMessage;
    },
    []
  );

  const handleOrderAction = useCallback(
    async (order: PartnerOrderData, actionType: PartnerOrderActionType) => {
      setActionOrderId(order.id);
      try {
        if (actionType === 'accept') {
          await partnerOfferService.acceptOrder(order.id);
          showPartnerSuccess(t('partner.orders.actions.acceptSuccess'));
        } else {
          await partnerOfferService.cancelOrder(order.id);
          showPartnerSuccess(t('partner.orders.actions.cancelSuccess'));
        }
        await loadOrders();
      } catch (error: any) {
        const fallbackMessage =
          actionType === 'accept'
            ? t('partner.orders.actions.acceptError')
            : t('partner.orders.actions.cancelError');
        showError(getApiErrorMessage(error, fallbackMessage));
      } finally {
        setActionOrderId(null);
      }
    },
    [t, loadOrders, getApiErrorMessage]
  );

  const handleFilterChange = useCallback((key: keyof OrdersFilters, value: string) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setCurrentPage(1);
  }, []);

  const handlePageChange = useCallback((page: number) => {
    setCurrentPage(page);
  }, []);

  const handlePageSizeChange = useCallback((newPageSize: number) => {
    setPageSize(newPageSize);
    setCurrentPage(1);
  }, []);

  const handleResetFilters = useCallback(() => {
    setFilters(DEFAULT_FILTERS);
    setLocalSearch('');
    setCurrentPage(1);
  }, []);

  // Content modal handlers
  const handleShowContent = useCallback((order: PartnerOrderData) => {
    setSelectedOrder(order);
    setIsContentModalOpen(true);
  }, []);

  const handleCloseContentModal = useCallback(() => {
    setIsContentModalOpen(false);
    setSelectedOrder(null);
  }, []);

  // Check if there are active filters
  const hasActiveFilters = useMemo(() => {
    return filters.search !== '' || filters.status !== 'all' || filters.offer !== 'all';
  }, [filters]);

  return (
    <div className="flex flex-col h-full bg-white">
      <div className="px-4 py-6 md:py-4 md:px-6">
        <Typography variant="h5" className="md:font-semibold text-gray-900">
          {t('partner.orders.title')}
        </Typography>
      </div>

      <FiltersSection
        hasActiveFilters={hasActiveFilters}
        filters={filters}
        localSearch={localSearch}
        availableOffers={availableOffers}
        offersLoading={offersLoading}
        onSearchChange={setLocalSearch}
        onFilterChange={handleFilterChange}
        onResetFilters={handleResetFilters}
        onPublicLinksClick={onPublicLinksOpen}
      />

      {/* Table */}
      <div className="px-4 md:px-6 md:h-full">
        <OrdersTable
          orders={orders}
          isLoading={isLoading}
          totalCount={totalCount}
          currentPage={currentPage}
          pageSize={pageSize}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          onShowContent={handleShowContent}
          onOrderAction={handleOrderAction}
          actionOrderId={actionOrderId}
        />
      </div>

      <PublicLinksModal
        isOpen={isPublicLinksOpen}
        onClose={onPublicLinksClose}
      />

      {/* Content Preview Modal */}
      {selectedOrder && (
        <ContentPreviewModal
          isOpen={isContentModalOpen}
          onClose={handleCloseContentModal}
          modelName={selectedOrder.model.username}
          mediaUrls={selectedOrder.content.mediaUrls}
          contentLinks={selectedOrder.content.contentLinks}
        />
      )}
    </div>
  );
};

export default Orders;
